import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'

import { HIDE_ON_MOBILE_INLINE, TEXT_TRUNCATE } from '../../theme/responsive'

interface Props {
  open: boolean
  email: string
  onClose: () => void
  onSubmit: (payload: { password: string; confirmPassword: string }) => Promise<void>
  isSubmitting?: boolean
}

const headerButtonSx = {
  minHeight: 28,
  px: 1,
  py: 0.25,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none',
} as const

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

const extractApiErrorMessage = (error: unknown): string => {
  if (!error || typeof error !== 'object') return 'Failed to update password. Please try again.'
  if (!('data' in error)) return 'Failed to update password. Please try again.'

  const data = (error as { data?: unknown }).data
  if (!data || typeof data !== 'object') return 'Failed to update password. Please try again.'

  const detail = (data as { detail?: unknown }).detail
  if (typeof detail === 'string' && detail.trim()) return detail
  return 'Failed to update password. Please try again.'
}

export default function SettingsChangePasswordDialog({ open, email, onClose, onSubmit, isSubmitting = false }: Props) {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setPassword('')
    setConfirmPassword('')
    setValidationError(null)
  }, [open, email])

  const handleClose = () => {
    setPassword('')
    setConfirmPassword('')
    setValidationError(null)
    onClose()
  }

  const handleSave = async () => {
    const normalizedEmail = email.trim()
    if (!normalizedEmail) {
      setValidationError('Please type Email!')
      return
    }
    if (!isValidEmail(normalizedEmail)) {
      setValidationError('Please type correct email format!')
      return
    }
    if (!password.trim()) {
      setValidationError('Please type Password!')
      return
    }
    if (password !== confirmPassword) {
      setValidationError('Confirm password is not correct!')
      return
    }

    setValidationError(null)
    try {
      await onSubmit({ password, confirmPassword })
      handleClose()
    } catch (error: unknown) {
      setValidationError(extractApiErrorMessage(error))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: 'min(900px, 92vh)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          pt: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
          flexWrap: 'nowrap',
        }}
      >
        <Typography
          component="span"
          noWrap
          title="Settings - Change Password"
          sx={{
            ...TEXT_TRUNCATE,
            flex: '1 1 auto',
            fontWeight: 700,
            fontSize: '1rem',
            minWidth: 0,
          }}
        >
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>
            Settings -{' '}
          </Box>
          Change Password
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
          <Button variant="contained" size="small" color="success" onClick={handleSave} disabled={isSubmitting} sx={headerButtonSx}>
            {isSubmitting ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button variant="outlined" size="small" onClick={handleClose} disabled={isSubmitting} sx={headerButtonSx}>
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent
        dividers
        sx={{
          pt: 0.75,
          overflowY: 'auto',
          flex: '1 1 auto',
          minHeight: 0,
          '& .MuiInputBase-root': { minHeight: 34, fontSize: '0.82rem' },
          '& .MuiInputBase-input': { py: 0.625, fontSize: '0.82rem' },
          '& .MuiInputLabel-root': { fontSize: '0.82rem' },
          '& .MuiTypography-body2': { fontSize: '0.82rem' },
        }}
      >
        {validationError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {validationError}
          </Alert>
        )}

        <Box sx={{ maxWidth: 560, mx: 'auto', pb: 1.5 }}>
          <Grid container spacing={0.75}>
            <Grid item xs={12}>
              <TextField label="Email" size="small" fullWidth value={email} disabled />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Password"
                size="small"
                fullWidth
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus={open}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Confirm Password"
                size="small"
                fullWidth
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

